import React, { useState, useEffect } from 'react';
import { RunState } from '../../api';
import runsService from '../../services/runs';
import { CollectionResult, Run as PortalRun } from '../../services/sessionModels';
import { PagingState, CustomPaging, SortingState, Sorting } from '@devexpress/dx-react-grid';
import { Grid, Table, TableHeaderRow, TableColumnResizing, PagingPanel } from '@devexpress/dx-react-grid-material-ui';
import Date from '../Util/Date';
import CardHeader from '../Util/CardHeader';
import moment from 'moment';
import { useSnackbar } from 'notistack';
import styled from 'styled-components';
import RunsFilter from './RunsFilter';
import { Tooltip } from '@material-ui/core';
import SearchOutlined from '@material-ui/icons/SearchOutlined';
import { Moment } from 'moment';
import { withRouter, RouteComponentProps } from 'react-router-dom';
import RunStatusValue from '../Util/RunStatusValue';
import useLocalStorage from '../Util/useLocalStorage';

interface Props extends RouteComponentProps {}

type Run = PortalRun & { pitch?: { id: number; name: string } | null };

const created = (row: Run) => {
  let milliseconds;
  if (row.creationTime) {
    milliseconds = moment.utc(row.creationTime).unix() * 1000;
  }
  return <Date value={milliseconds} format="DD-MM-YYYY HH:mm" />;
};

const rundate = (row: Run) => {
  let milliseconds;
  if (row.startTime) {
    milliseconds = moment.utc(row.startTime).unix() * 1000;
  }
  return <Date value={milliseconds} format="DD-MM-YYYY HH:mm" />;
};

const status = (row: Run) => {
  return <RunStatusValue value={row.state as RunState} />;
};

const pitch = (row: Run) => {
  return row.pitch?.name || '-';
};
/*
const deleteRunValue = (row: Run) => {
  return <DeleteRun runState={row.status.state} runid={row.id || '-'} />;
};
*/

const sortingStateColumnExtensions = [
  { columnName: 'components', sortingEnabled: false },
  { columnName: 'runs.bootTime', sortingEnabled: false },
];

const TitleWrapper = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
`;

const Body = styled.div`
  display: flex;
  flex-direction: row;
`;

const FilterContainer = styled.div`
  min-width: 300px;
  flex-grow: 1;
`;

const TableContainer = styled.div`
  flex-grow: 10;
  margin-left: 32px;
  width: calc(100vw - 850px);
`;

const sortval = (
  columnname: string,
  columns: Array<{ name: string; title: string; getCellValue?: any; sort?: any }>
) => {
  const c = columns.find((column) => column.name === columnname);
  if (c && c.sort) {
    return c.sort;
  }
  return columnname;
};

const AvailableRuns: React.FunctionComponent<Props> = (props) => {
  const [sorting, setSorting] = useLocalStorage<Sorting[]>('availableRuns-sort', [
    { columnName: 'created', direction: 'desc' as 'desc' },
  ]);
  const [page, setPage] = useLocalStorage<number>('availableRuns-page', 0);
  const [pageSize, setPageSize] = useLocalStorage<number>('availableRuns-pagesize', 10);
  const [columnWidths, setColumnWidths] = useState<Array<{ columnName: string; width: number | string }>>([
    { columnName: 'id', width: 240 },
    { columnName: 'created', width: 180 },
    { columnName: 'status', width: 120 },
    { columnName: 'rundate', width: 180 },
    { columnName: 'pitch', width: 180 },
    //{ columnName: 'deleteRun', width: 80 },
  ]);
  const [completedOnly, setCompletedOnly] = useState(false);
  const [search, setSearch] = useState<string | undefined>();
  const [statuses, setStatuses] = useState<Array<RunState>>([]);
  const [creationDateRange, setCreationDateRange] = useState<{ startDate: Moment | null; endDate: Moment | null }>({
    startDate: null,
    endDate: null,
  });
  const [runDateRange, setRunDateRange] = useState<{ startDate: Moment | null; endDate: Moment | null }>({
    startDate: null,
    endDate: null,
  });
  const [pitches, setPitches] = useState<{ [key: string]: boolean }>({});
  const [showFilter, setShowFilter] = useState(false);
  let columns: any = [
    { name: 'id', title: 'Id', prio: 1 },
    { name: 'created', title: 'Created', getCellValue: created, sort: 'creationTime', prio: 5 },
    { name: 'status', title: 'Status', getCellValue: status, sort: 'state', prio: 3 },
    { name: 'rundate', title: 'Run date', getCellValue: rundate, sort: 'startTime', prio: 6 },
    { name: 'pitch', title: 'Pitch', getCellValue: pitch, sort: 'pitch.name', prio: 8 },
    //{ name: 'deleteRun', title: ' ', getCellValue: deleteRunValue, prio: 4 },
  ];

  const [data, setData] = useState<CollectionResult<Run> | null>(null);
  const [error, setError] = useState<unknown>(null);
  const sort = sorting[0];
  const orderBy = sort ? sortval(sort.columnName, columns) : 'creationTime';
  const sortOrder = sort?.direction === 'asc' ? 'Ascending' : 'Descending';
  const pitchIds = Object.keys(pitches).join(',');
  const effectiveStatuses = completedOnly ? [RunState.Stopped] : statuses;
  const statusKey = effectiveStatuses.join(',');
  const creationStart = creationDateRange.startDate?.toISOString();
  const creationEnd = creationDateRange.endDate?.toISOString();
  const runStart = runDateRange.startDate?.toISOString();
  const runEnd = runDateRange.endDate?.toISOString();

  useEffect(() => {
    let active = true;
    const params = new URLSearchParams();
    params.set('offset', String(page * pageSize));
    params.set('limit', String(pageSize));
    params.set('orderBy', orderBy);
    params.set('sortOrder', sortOrder);
    if (search) params.set('searchText', search);
    statusKey.split(',').filter(Boolean).forEach((state) => params.append('states', state));
    pitchIds.split(',').filter(Boolean).forEach((id) => params.append('pitchIds', id));
    if (creationStart) params.set('creationStart', creationStart);
    if (creationEnd) params.set('creationEnd', creationEnd);
    if (runStart) params.set('runStart', runStart);
    if (runEnd) params.set('runEnd', runEnd);
    runsService.getRuns(params).then(
      (result: CollectionResult<Run>) => active && setData(result),
      (err: unknown) => active && setError(err)
    );
    return () => {
      active = false;
    };
  }, [page, pageSize, orderBy, sortOrder, search, statusKey, pitchIds, creationStart, creationEnd, runStart, runEnd]);

  useEffect(() => {
    if (data && Math.floor(data.total / pageSize) < page) {
      setPage(0);
    }
  }, [data, page, pageSize, setPage]);

  const { enqueueSnackbar } = useSnackbar();

  if (error) {
    enqueueSnackbar('Failed to load runs', { variant: 'error', autoHideDuration: 3000 });
  }

  const griddata = data?.items || [];
  const count = data?.total || 0;
  return (
    <div className="sciui-card sciui-m-l sciui-p-l sciui-bg-p-5" style={{ overflow: 'visible' }}>
      <TitleWrapper>
        <div style={{ display: 'flex' }}>
          <CardHeader>Available runs</CardHeader>
          <div style={{ marginTop: '30px' }}>
            <Tooltip title="Show filter">
              <SearchOutlined
                style={{ fontSize: 30, marginLeft: '16px', marginTop: '10px', cursor: 'pointer' }}
                onClick={() => setShowFilter(!showFilter)}
              />
            </Tooltip>
          </div>
        </div>
      </TitleWrapper>
      <Body>
        {showFilter && (
          <FilterContainer>
            <RunsFilter
              key="filter"
              creationDateRange={creationDateRange}
              onCreationDateRangeChange={setCreationDateRange}
              runDateRange={runDateRange}
              onRunDateRangeChange={setRunDateRange}
              search={search}
              onSearchChange={setSearch}
              statuses={statuses}
              onStatusesChange={setStatuses}
              completedOnly={completedOnly}
              onCompletedOnlyChange={setCompletedOnly}
              pitches={pitches}
              onPitchesChange={setPitches}
            />
          </FilterContainer>
        )}
        <TableContainer>
          <Grid rows={griddata} columns={columns}>
            <PagingState
              currentPage={page}
              onCurrentPageChange={setPage}
              pageSize={pageSize}
              onPageSizeChange={setPageSize}
            />
            <SortingState
              sorting={sorting}
              onSortingChange={setSorting}
              columnExtensions={sortingStateColumnExtensions}
            />
            <CustomPaging totalCount={count} />
            <Table
              rowComponent={(row) => (
                <Table.Row
                  {...row}
                  hover
                  // eslint-disable-next-line no-alert
                  onClick={() => props.history.push('/runs/' + row.row.id)}
                />
              )}
            />
            <TableColumnResizing columnWidths={columnWidths} onColumnWidthsChange={setColumnWidths} />
            <TableHeaderRow showSortingControls />
            <PagingPanel pageSizes={[10, 25, 50]} />
          </Grid>
        </TableContainer>
      </Body>
    </div>
  );
};

export default withRouter(AvailableRuns);
